import BoxInfo from '@/components/boxInfo/boxInfo';
import { ButtonSaveEdition } from '@/modules/move/components/buttonSaveEdition/buttonSaveEdition';
import DialogOpeningHoursMessage from '@/modules/move/components/dialogOpeningHoursMessage/dialogOpeningHoursMessage';
import { DialogRowEdition } from '@/modules/move/components/dialogRowEdition/dialogRowEdition';
import { ReportList } from '@/modules/move/components/reportList/reportList';
import ReportReviewList from '@/modules/move/components/reportReviewList/reportReviewList';
import { countStatusDefault } from '@/modules/move/entities/constants/moveRow';
import { MOVE_SHEET_COLUMN } from '@/modules/move/entities/constants/moveSheetColumn';
import { MOVE_ROW_BENEFICIARY_STATUS } from '@/modules/move/entities/enums/move';
import { MoveNormalized } from '@/modules/move/entities/interfaces/move';
import {
  MoveSheetContent,
  MoveSheetError,
  MoveSheetNormalized,
  MoveSheetParams,
} from '@/modules/move/entities/interfaces/moveSheet';
import { useExportMoveRows } from '@/modules/move/hooks/useExportRows/useExportMoveRows';
import { useMoveEdition } from '@/modules/move/hooks/useMoveEdition/useMoveEdition';
import { MoveRowService } from '@/modules/move/services/moveRowService';
import { BeneficiaryStatusNumbers } from '@/modules/move/views/countBeneficiaryStatus/beneficiaryStatusNumbers';
import { useHistoryNavigator } from '@/navigation';
import ErrorTableModal from '@/pages/beneficiaries/removal/components/errorTableModal';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import { useRequestReadOne } from '@/shared/hooks/useRequestData/useRequestReadOne';
import ICONS from '@/shared/entities/constants/icons';
import React, { useCallback, useMemo, useState } from 'react';
import { Button, Text, Toggle } from 'vkit/lib/components';
import { Grid, Tooltip } from 'vkit/lib/context';

interface ReportProps {
  move?: MoveNormalized;
  loading?: boolean;
}

export const Report: React.FC<ReportProps> = ({ move, loading: loadingMove }) => {
  const [onlyErrors, setOnlyErrors] = useState<MoveSheetParams['onlyErrors']>(1);
  const [rowToEdition, setRowToEdition] = useState<MoveSheetContent | null>(null);
  const [moveRowError, setMoveRowError] = useState<MoveSheetError>({});
  const [moveRow, setMoveRow] = useState<MoveSheetNormalized | null>(null);
  const [onlyFieldWithError, setOnlyFieldWithError] = useState(false);
  const [lineNumber, setLineNumber] = useState<string>('0');
  const { companyId } = useCompany();
  const navigate = useHistoryNavigator();

  const {
    editedRowByLine,
    updateEditedRow,
    errorDetails,
    setErrorDetails,
    loading,
    saveEditedRow,
    openingHours,
    showDialogOpeningHoursMessage,
    setShowDialogOpeningHoursMessage,
  } = useMoveEdition({ move });
  const { downloadRows, loading: loadingDownload } = useExportMoveRows({ move });

  const { data: countRowByBeneficiaryStatus = countStatusDefault, loading: loadingStatus } =
    useRequestReadOne({
      externalLoading: loadingMove,
      source: () => {
        if (move?.id) {
          return new MoveRowService().getCountRowsByBeneficiaryStatus({ fileId: move.id });
        }
      },
    });

  const countRowError =
    countRowByBeneficiaryStatus[MOVE_ROW_BENEFICIARY_STATUS.PROCESSED_WITH_ERROR] +
    countRowByBeneficiaryStatus[MOVE_ROW_BENEFICIARY_STATUS.VALIDATED_WITH_ERROR];

  const fieldsThatHaveError: MOVE_SHEET_COLUMN[] = useMemo(
    () => [
      MOVE_SHEET_COLUMN.contrato,
      MOVE_SHEET_COLUMN.subfatura,
      MOVE_SHEET_COLUMN.nome,
      MOVE_SHEET_COLUMN.dataNascimento,
      MOVE_SHEET_COLUMN.matricula,
      MOVE_SHEET_COLUMN.cpf,
      MOVE_SHEET_COLUMN.cpfTitular,
      MOVE_SHEET_COLUMN.sexo,
      MOVE_SHEET_COLUMN.estadoCivil,
      MOVE_SHEET_COLUMN.dataInicioVigencia,
      MOVE_SHEET_COLUMN.nomeMae,
      MOVE_SHEET_COLUMN.cep,
      MOVE_SHEET_COLUMN.uf,
      MOVE_SHEET_COLUMN.logradouro,
      MOVE_SHEET_COLUMN.bairro,
      MOVE_SHEET_COLUMN.cidade,
      MOVE_SHEET_COLUMN.complemento,
      MOVE_SHEET_COLUMN.numero,
      MOVE_SHEET_COLUMN.plano,
      MOVE_SHEET_COLUMN.emailSegurado,
      MOVE_SHEET_COLUMN.grauParentesco,
      MOVE_SHEET_COLUMN.cns,
      MOVE_SHEET_COLUMN.dataAdmissao,
      MOVE_SHEET_COLUMN.codAngariador,
      MOVE_SHEET_COLUMN.dataAngariacao,
      MOVE_SHEET_COLUMN.dataCasamento,
      MOVE_SHEET_COLUMN.codElegibilidade,
      MOVE_SHEET_COLUMN.tipoContaBancaria,
      MOVE_SHEET_COLUMN.creditoBanco,
      MOVE_SHEET_COLUMN.creditoNumAgencia,
      MOVE_SHEET_COLUMN.creditoDVAgencia,
      MOVE_SHEET_COLUMN.creditoNumConta,
      MOVE_SHEET_COLUMN.creditoDVConta,
    ],
    [],
  );

  const extractErrors = useCallback(
    (moveRow: MoveSheetNormalized) => {
      if (!moveRow.content) {
        return {};
      }

      return moveRow.content.reduce((acc: MoveSheetError, field) => {
        if (!field.messages.length || !fieldsThatHaveError.includes(field.key)) {
          return acc;
        }

        const messageErrors = field.messages.map((message) => message.value);
        acc[field.key] = messageErrors.join(' | ');

        return acc;
      }, {});
    },
    [fieldsThatHaveError],
  );

  return (
    <>
      <DialogRowEdition
        setRowToEdition={setRowToEdition}
        rowToEdition={rowToEdition}
        setRowError={setMoveRowError}
        rowError={moveRowError}
        originalRow={moveRow}
        editedRow={editedRowByLine[lineNumber]}
        editedLineNumbers={Object.keys(editedRowByLine)}
        onlyFieldWithError={onlyFieldWithError}
        updateRow={updateEditedRow}
        line={lineNumber}
      />

      <DialogOpeningHoursMessage
        open={showDialogOpeningHoursMessage}
        setOpen={(show) => {
          setShowDialogOpeningHoursMessage(show);
          if (!show) {
            navigate.push(`/companies/${companyId}/beneficiaries/moves`);
          }
        }}
        openingTime={openingHours.openingTime}
        closingTime={openingHours.closingTime}
      />

      <ErrorTableModal
        details={errorDetails.filter((error) => !error.skippable)}
        setErrorDetails={setErrorDetails}
      />

      <BoxInfo
        title='Validação do arquivo'
        icon='trending-up-outline'
        footer={
          Boolean(countRowError) && (
            <ButtonSaveEdition
              countEditedRow={
                Object.values(editedRowByLine).filter((editedRow) => editedRow.accountable).length
              }
              countRowWithError={countRowError}
              onClick={saveEditedRow}
              loading={loading}
            />
          )
        }>
        <Grid column gap={16}>
          <Text title value='Status das vidas' />

          <BoxInfo margin='0' elevation={0} padding='8px 8px 8px 16px'>
            <Grid alignContent='justify' alignItems='center' stretch>
              <BeneficiaryStatusNumbers
                loading={loadingStatus}
                statuses={countRowByBeneficiaryStatus}
              />

              <Tooltip
                title={
                  !countRowByBeneficiaryStatus?.processed
                    ? 'Nenhuma vida foi concluída ainda, aguarde um pouco mais!'
                    : undefined
                }>
                <Button
                  testId='download-processed-rows'
                  loading={loadingStatus || loadingDownload}
                  disabled={
                    loadingStatus ||
                    loadingDownload ||
                    !Boolean(countRowByBeneficiaryStatus?.processed)
                  }
                  iconColor={countRowByBeneficiaryStatus?.processed ? 'colorDefault' : 'light'}
                  icon={ICONS.DOWNLOAD}
                  label='Download dos beneficiários concluídos'
                  onClick={() => {
                    downloadRows({
                      fileId: move?.id,
                      beneficiaryStatus: MOVE_ROW_BENEFICIARY_STATUS.PROCESSED,
                    });
                  }}
                  solo
                  outlined
                />
              </Tooltip>
            </Grid>
          </BoxInfo>

          <Grid alignContent='justify' stretch>
            <Text title value='Linhas' />

            <Grid>
              <Toggle
                checked={onlyErrors === 1}
                handleChange={() => {
                  setOnlyErrors((currentValue) => (currentValue === 1 ? undefined : 1));
                }}
                small
              />
              <Text size='medium' margin='0 0 0 8px' value='Mostrar apenas erros e críticas' />
            </Grid>
          </Grid>

          <div style={{ width: '100%' }}>
            <ReportList
              onlyErrors={onlyErrors}
              moveId={move?.id}
              editedRowByLine={editedRowByLine}
              setRowToEdition={(moveRow) => {
                const line = moveRow.line.toString();
                const errors = extractErrors(moveRow);
                setLineNumber(line);
                setRowToEdition(editedRowByLine[line]?.fields || moveRow.originalContent);
                setMoveRow(moveRow);
                setMoveRowError(errors);
              }}
            />
          </div>

          {Boolean(onlyErrors) && (
            <div style={{ width: '100%' }}>
              <ReportReviewList
                moveId={move?.id}
                editedRowByLine={editedRowByLine}
                setRowToEdition={(moveRow) => {
                  const line = moveRow.line.toString();
                  setLineNumber(line);
                  setRowToEdition(editedRowByLine[line]?.fields || moveRow.originalContent);
                  setMoveRow(moveRow);
                  setMoveRowError(extractErrors(moveRow));
                  setOnlyFieldWithError(false);
                }}
              />
            </div>
          )}
        </Grid>
      </BoxInfo>
    </>
  );
};
